import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Plus, Sparkles, ShieldCheck } from "lucide-react";
import { api, errMsg, pollJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const PSTATUS = {
  draft: ["Draft", "bg-muted text-muted-foreground"], pending_approval: ["Awaiting authorisation", "bg-[#FFA000]/15 text-[#B87300]"], approved: ["Authorised (not started)", "bg-sky-500/15 text-sky-700 dark:text-sky-300"],
  active: ["Active", "bg-health/15 text-health"], suspended: ["Suspended", "bg-alert/15 text-alert"], closed: ["Closed", "bg-slate-500/15 text-slate-600 dark:text-slate-300"],
  expired: ["Expired", "bg-alert/10 text-alert"], cancelled: ["Cancelled", "bg-muted text-muted-foreground"],
};
export const PermitStatus = ({ s }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PSTATUS[s]?.[1]}`} data-testid="permit-status">{PSTATUS[s]?.[0] || s}</span>;
export const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const local = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const NewPermit = ({ open, setOpen, authorisers }) => {
  const nav = useNavigate();
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ type: "Hot Works", location: "", description: "", valid_from: local(new Date()), valid_to: local(new Date(Date.now() + 8 * 36e5)), authoriser_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/permits/types").then((r) => setTypes(r.data));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async (useAi) => {
    setBusy(true);
    try {
      const body = { ...f, authoriser_id: f.authoriser_id || null, valid_from: new Date(f.valid_from).toISOString(), valid_to: new Date(f.valid_to).toISOString(), use_ai: useAi, client_request_id: crypto.randomUUID() };
      const { data } = await api.post("/permits", body);
      const id = data.permit_id || (await pollJob(data.job_id)).permit_id;
      toast.success("Draft permit created — check every item before submitting");
      nav(`/app/permits/${id}`);
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
      <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>New permit to work</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Permit type</Label><select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={f.type} onChange={set("type")} data-testid="permit-type">{types.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="space-y-1"><Label>Work location</Label><Input value={f.location} onChange={set("location")} placeholder="Plant room, Level 2, Unit 4 Riverside Park" data-testid="permit-location" /></div>
          <div className="space-y-1"><Label>Describe the work</Label><Textarea rows={4} value={f.description} onChange={set("description")} placeholder="Welding repair to a steel handrail bracket next to the loading bay. Two operatives, gas cylinders on a trolley." data-testid="permit-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Valid from</Label><Input type="datetime-local" value={f.valid_from} onChange={set("valid_from")} data-testid="permit-valid-from" /></div>
            <div className="space-y-1"><Label>Valid to</Label><Input type="datetime-local" value={f.valid_to} onChange={set("valid_to")} data-testid="permit-valid-to" /></div>
          </div>
          <div className="space-y-1"><Label>Named authorised person</Label>
            <select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={f.authoriser_id} onChange={set("authoriser_id")} data-testid="permit-authoriser"><option value="">Choose later</option>{authorisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
            {authorisers.length === 0 && <p className="text-xs text-alert">No authorised persons set yet — a permit manager must add them first.</p>}</div>
          <p className="text-xs text-muted-foreground">AI drafts the hazards, precautions, isolations, checks and PPE (2 credits, charged only on success). Work cannot start until the named person authorises it.</p>
          <div className="flex gap-2">
            <Button className="flex-1" disabled={busy || f.description.length < 10} onClick={() => go(true)} data-testid="permit-ai-submit"><Sparkles className="mr-1.5 h-4 w-4" />{busy ? "Drafting permit…" : "Draft with AI"}</Button>
            <Button variant="outline" disabled={busy} onClick={() => go(false)} data-testid="permit-blank-submit">Blank permit</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Authorisers = ({ open, setOpen, current, reload }) => {
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    if (open) {
      api.get("/org/members").then((r) => setMembers(r.data));
      setSel(current.map((a) => a.id));
    }
  }, [open, current]);
  const save = async () => {
    try {
      await api.put("/permits/authorisers", { user_ids: sel });
      toast.success("Authorised persons updated");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}><DialogContent><DialogHeader><DialogTitle>Authorised persons</DialogTitle></DialogHeader>
      <p className="text-sm text-muted-foreground">Only people on this list can be named to authorise permits. Choose people who are competent and appointed to do so.</p>
      <ul className="max-h-64 space-y-1 overflow-auto">{members.map((m) => <li key={m.id}><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sel.includes(m.id)} onChange={(e) => setSel(e.target.checked ? [...sel, m.id] : sel.filter((x) => x !== m.id))} data-testid={`authoriser-${m.id}`} />{m.name}<span className="text-xs text-muted-foreground">{m.role}</span></label></li>)}</ul>
      <Button onClick={save} data-testid="authorisers-save">Save</Button></DialogContent></Dialog>
  );
};

export default function Permits() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [auth, setAuth] = useState([]);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [filter, setFilter] = useState("");
  const load = () => {
    api.get("/permits").then((r) => setList(r.data)).catch(() => setList([]));
    api.get("/permits/authorisers").then((r) => setAuth(r.data));
  };
  useEffect(() => {
    load();
  }, []);
  const canCreate = ["owner", "admin", "hse_manager", "supervisor", "project_manager"].includes(user.role);
  const canManage = ["owner", "admin", "hse_manager"].includes(user.role);
  if (!list) return <Loader />;
  const shown = filter ? list.filter((p) => p.status === filter) : list;
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="permits-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">High-risk work control</p><h1 className="mt-2 text-3xl font-bold">Permits to work</h1>
          <p className="text-sm text-muted-foreground">AI can draft a permit, but it only becomes active when the named authorised person signs it off.</p></div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/app/permits/board" data-testid="open-permit-board">Live board</Link></Button>
          {canManage && <Button variant="outline" onClick={() => setManage(true)} data-testid="manage-authorisers"><ShieldCheck className="mr-1.5 h-4 w-4" />Authorised persons ({auth.length})</Button>}
          {canCreate && <Button onClick={() => setOpen(true)} data-testid="new-permit"><Plus className="mr-1.5 h-4 w-4" />New permit</Button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{[["", "All"], ...Object.entries(PSTATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 text-xs ${filter === k ? "border-health bg-health/10" : ""}`} data-testid={`permit-filter-${k || "all"}`}>{l}</button>)}</div>
      {shown.length === 0 ? <EmptyState icon={KeyRound} title="No permits" text="Raise a permit for hot works, confined spaces, isolations and other high-risk work." /> : (
        <div className="overflow-x-auto rounded-2xl border bg-card"><table className="w-full text-sm"><thead className="border-b text-left text-xs text-muted-foreground"><tr><th className="p-3">Ref</th><th className="p-3">Permit</th><th className="p-3">Location</th><th className="p-3">Valid</th><th className="p-3">Status</th></tr></thead>
          <tbody>{shown.map((p) => <tr key={p.id} className="border-b last:border-0 hover:bg-accent/50"><td className="p-3 font-mono text-xs">{p.ref}</td><td className="p-3"><Link to={`/app/permits/${p.id}`} className="font-medium hover:underline" data-testid={`permit-row-${p.id}`}>{p.title}</Link><p className="text-xs text-muted-foreground">{p.type}</p></td><td className="p-3 text-muted-foreground">{p.location || "—"}</td><td className="p-3 text-xs text-muted-foreground">{fmt(p.valid_from)} → {fmt(p.valid_to)}</td><td className="p-3"><PermitStatus s={p.status} /></td></tr>)}</tbody></table></div>
      )}
      <NewPermit open={open} setOpen={setOpen} authorisers={auth} />
      <Authorisers open={manage} setOpen={setManage} current={auth} reload={load} />
    </div>
  );
}
